package com.kinoshazam.taste

import com.kinoshazam.TASTE_LAST_SESSION
import com.kinoshazam.TASTE_SEEDS
import com.kinoshazam.data.Storage
import io.github.aakira.napier.Napier
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json

class TasteInteractor(
    private val tasteRepository: TasteRepository,
    private val storage: Storage,
) {

    private val json = Json { ignoreUnknownKeys = true }

    suspend fun saveSession(request: TasteSessionRequest): TasteSessionDto? {
        storage.put(TASTE_LAST_SESSION, json.encodeToString(request))
        return runCatching { tasteRepository.saveSession(request) }
            .onFailure { Napier.e("taste saveSession: ${it.message}") }
            .getOrNull()
    }

    suspend fun replaceSeeds(items: List<TasteTitleRef>): List<TasteTitleRef> {
        storage.put(TASTE_SEEDS, json.encodeToString(items))
        return runCatching { tasteRepository.replaceSeeds(items) }
            .onFailure { Napier.e("taste replaceSeeds: ${it.message}") }
            .getOrDefault(items)
    }

    fun cachedSeeds(): List<TasteTitleRef> {
        val raw = storage.get<String>(TASTE_SEEDS) ?: return emptyList()
        return runCatching { json.decodeFromString<List<TasteTitleRef>>(raw) }.getOrDefault(emptyList())
    }

    suspend fun recordSignal(
        videoId: String,
        isTvShow: Boolean,
        kind: TasteSignalKind,
        sessionId: String? = null,
    ) {
        runCatching {
            tasteRepository.addSignal(
                TasteSignalRequest(
                    videoId = videoId,
                    isTvShow = isTvShow,
                    kind = kind,
                    sessionId = sessionId,
                )
            )
        }.onFailure { Napier.e("taste signal $kind: ${it.message}") }
    }

    suspend fun seedCandidates(page: Int = 1): List<TasteCatalogItem> {
        return runCatching { tasteRepository.seedCandidates(page) }
            .onFailure { Napier.e("taste seedCandidates: ${it.message}") }
            .getOrDefault(emptyList())
    }

    suspend fun profile(): TasteProfileDto {
        return runCatching { tasteRepository.profile() }
            .onFailure { Napier.e("taste profile: ${it.message}") }
            .getOrNull()
            ?: TasteProfileDto(
                seeds = cachedSeeds(),
                completeness = TasteCompleteness(
                    hasSession = storage.get<String>(TASTE_LAST_SESSION) != null,
                    seedCount = cachedSeeds().size,
                ),
            )
    }

    suspend fun mergeDeviceOnLogin() {
        runCatching { tasteRepository.mergeDevice() }
            .onFailure { Napier.e("taste merge: ${it.message}") }
    }

    suspend fun pick(exclude: List<TasteTitleRef> = emptyList()): TastePickDto? {
        return runCatching { tasteRepository.pick(exclude) }
            .onFailure { Napier.e("taste pick failed", it) }
            .getOrNull()
    }

    suspend fun createParty(request: TastePartyCreateRequest): TastePartyDto? {
        return runCatching { tasteRepository.createParty(request) }
            .onFailure { Napier.e("taste createParty: ${it.message}") }
            .getOrNull()
    }

    suspend fun party(code: String): TastePartyDto? {
        return runCatching { tasteRepository.party(code) }
            .onFailure { Napier.e("taste party: ${it.message}") }
            .getOrNull()
    }

    suspend fun joinParty(code: String, displayName: String? = null): TastePartyDto? {
        return runCatching { tasteRepository.joinParty(code, displayName) }
            .onFailure { Napier.e("taste joinParty: ${it.message}") }
            .getOrNull()
    }

    suspend fun setPartyReady(code: String, ready: Boolean = true): TastePartyDto? {
        return runCatching { tasteRepository.setPartyReady(code, ready) }
            .onFailure { Napier.e("taste partyReady: ${it.message}") }
            .getOrNull()
    }

    suspend fun leaveParty(code: String) {
        runCatching { tasteRepository.leaveParty(code) }
            .onFailure { Napier.e("taste leaveParty: ${it.message}") }
    }

    suspend fun partyPick(code: String, exclude: List<TasteTitleRef> = emptyList()): TastePartyDto? {
        return runCatching { tasteRepository.partyPick(code, exclude) }
            .onFailure { Napier.e("taste partyPick: ${it.message}") }
            .getOrNull()
    }

    suspend fun partySignal(
        code: String,
        videoId: String,
        isTvShow: Boolean,
        kind: TasteSignalKind,
        sessionId: String? = null,
    ): TastePartyDto? {
        return runCatching {
            tasteRepository.partySignal(
                code,
                TasteSignalRequest(
                    videoId = videoId,
                    isTvShow = isTvShow,
                    kind = kind,
                    sessionId = sessionId,
                ),
            )
        }.onFailure { Napier.e("taste partySignal $kind: ${it.message}") }
            .getOrNull()
    }

    suspend fun setPartyCapacity(code: String, capacity: Int): TastePartyDto? {
        return runCatching { tasteRepository.setPartyCapacity(code, capacity) }
            .onFailure { Napier.e("taste setPartyCapacity: ${it.message}") }
            .getOrNull()
    }

    suspend fun lockPartyPick(code: String): TastePartyDto? {
        return runCatching { tasteRepository.lockPartyPick(code) }
            .onFailure { Napier.e("taste lockPartyPick: ${it.message}") }
            .getOrNull()
    }
}
